import React, { useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import _Button from "../generic/_Button";
import PropTypes from "prop-types";
import { dateFormat } from "../../utils/dateHelpers";
import { HEIGHTS } from "../../utils/globals";
import { useFindItem } from "../../hooks/useFindItem";
import { getMessageSettings } from "../../utils/helpers";
import { useReplaceEmoji } from "../campaigns/hooks/useReemplaceEmoji";
import Alert from "react-bootstrap/Alert";
import Icon from "../Icon";

export const MessagePreview = (props) => {
  const { step = 1, data } = props;
  const replace = useReplaceEmoji();

  const isImage = (url) => {
    const ext = url.split(".").pop();

    if (!ext) return false;

    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return false;

    if (["pdf"].includes(ext)) return true;
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-2">
        <div className="flex flex-col justify-between bg-gray-200 rounded-lg px-4 py-2">
          {[2, 3].includes(step) ? (
            <span className="text-md font-semibold">
              {data.title_rcs_message}
            </span>
          ) : null}

          <span className="text-[12px] font-semibold">
            {replace(data.rcsmessage)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 my-2">
        {data.buttonActions && data.buttonActions.length > 0 && (
          <div className="flex flex-col justify-between bg-gray-200 rounded-lg px-3 py-2">
            <span className="text-sm font-bold text-gray-600 my-1">
              Acciones
            </span>
            <div className="flex flex-wrap gap-1 text-md font-semibold">
              {data.buttonActions.map((action, index) => (
                <span
                  key={index}
                  className="bg-blue-400 text-[10px] text-white rounded-lg px-3 py-1"
                >
                  {action.text}
                </span>
              ))}
            </div>
          </div>
        )}

        {data.buttonQuickReplies && data.buttonQuickReplies.length > 0 && (
          <div className="flex flex-col justify-between bg-gray-200 rounded-lg px-3 py-2">
            <div className="text-sm font-bold text-gray-600 my-1">
              Respuestas rápidas
            </div>
            <div className="flex flex-wrap gap-1 text-md font-semibold">
              {data.buttonQuickReplies.map((action, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-[10px] text-gray-600 rounded-lg px-3 py-1"
                >
                  {action.text}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {data?.imageUrl ? (
        <div className="flex flex-col justify-between bg-gray-200 rounded-lg px-3 py-2">
          <span className="text-sm font-bold text-gray-600 my-1">
            Archivo adjunto
          </span>
          <div className="w-full flex justify-center p-1">
            {isImage(data?.imageUrl) ? (
              <iframe
                src={data?.imageUrl}
                className="w-full rounded-md"
                style={{ height: "300px" }}
              />
            ) : (
              <img
                src={data?.imageUrl}
                alt="Preview"
                className="h-auto w-full rounded-md object-contain"
                style={{ maxHeight: "300px" }}
              />
            )}
          </div>
          <div className="flex justify-start gap-1 w-full text-sm text-gray-700">
            <span className="font-semibold text-sm">Resolución:</span>
            <span className="font-bold text-md">
              {HEIGHTS.find((item) => item.value === data.height)?.label ??
                "---"}
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
};

const _ModalConfirmRcs = (props) => {
  const {
    title = "",
    description = "",
    visible,
    onClose,
    onAction,
    isLoading,
    data,
  } = props;
  const item = useFindItem(data.listDatabases, "uiddatabase", data.uiddatabase);

  const _onAction = () => {
    if (onAction) onAction();

    onClose();
  };

  const renderMessage = (step) => {
    switch (step) {
      case 1:
        return (
          <>
            <MessagePreview step={step} data={data} />{" "}
          </>
        );

      case 2:
        return (
          <>
            <MessagePreview step={step} data={data} />{" "}
          </>
        );

      case 3:
        return (
          <>
            {data.carrousel &&
              data.carrousel.map((item, index) => {
                return (
                  <div key={index} className="mb-4 border rounded-lg p-1">
                    <div className="text-md font-bold text-gray-700 bg-gray-50 p-1 rounded-lg mb-2">
                      Mensaje {index + 1}
                    </div>
                    <MessagePreview
                      step={step}
                      data={{
                        ...item,
                        rcsmessage: item.description,
                        title_rcs_message: item.title,
                      }}
                    />
                  </div>
                );
              })}
          </>
        );

      default:
        return null;
    }
  };

  const { count, maxCharacters, messages } = useMemo(() => {
    const { limit } = getMessageSettings(data.fallback);
    return {
      count: data.fallback ? data.fallback.length : 0,
      maxCharacters: limit,
      messages: data.fallback ? Math.ceil(data.fallback.length / limit) : 0,
    };
  }, [data.fallback]);

  return (
    <Modal show={visible} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="text-xl font-semibold text-gray-600">{title}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="Notice">
          <div className="flex gap-2 items-center justify-start">
            <Icon icon="warning" className="text-yellow-800" />
            <span className="text-md font-normal text-yellow-800">
              {description}
            </span>
          </div>
        </Alert>

        <div className="space-y-2">
          <div className="bg-gray-100 rounded-lg px-4 py-2">
            <div className="font-bold text-lg text-gray-600">
              Datos Generales
            </div>

            <div className="flex justify-between">
              <span className="text-sm font-semibold text-gray-500">
                Campaña:
              </span>
              <span className="text-md font-semibold">{data.campaignName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-gray-500">
                Base de datos:
              </span>
              <span className="text-md font-semibold">
                {item?.name ?? "---"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-gray-500">
                Audiencia:
              </span>
              <span className="text-md font-semibold">
                {item?.records ?? "---"}
              </span>
            </div>
          </div>

          <div className="border rounded-lg px-4 py-2">
            <div className="font-bold text-lg text-gray-600">Mensaje RCS</div>
            {renderMessage(data.step)}
          </div>

          <div className="border rounded-lg px-4 py-2 flex flex-col">
            <div className="font-bold text-lg text-gray-600 ">Fallback</div>
            <div className="bg-gray-200 rounded-lg px-4 py-2">
              <span className="text-[12px] font-semibold text-gray-800">
                {data?.fallback ?? "---"}
              </span>
            </div>

            <span className="text-[10px] font-semibold   text-yellow-800 my-1">
              {count}/{maxCharacters} Caracteres (Se enviaran {messages} SMS)
            </span>
          </div>

          <div className=" bg-gray-100 rounded-lg px-4 py-2">
            <div className="font-bold text-lg text-gray-600">Programación</div>
            {data.isScheduled ? (
              <div className="px-4 py-2 flex justify-between">
                <div className="flex justify-start gap-1 items-center">
                  <Icon
                    icon="calendar_today"
                    size={24}
                    className={"p-2 rounded-lg bg-white border"}
                  />
                  <div>
                    <span className="text-[10px] font-semibold text-gray-400 flex items-center gap-1">
                      Fecha de Envio:
                    </span>
                    <span className="text-md font-semibold">
                      {dateFormat(data.dateSend, "DD/MM/YYYY")}
                    </span>
                  </div>
                </div>
                <div className="flex justify-start gap-1 items-center">
                  <Icon
                    icon="timer"
                    size={24}
                    className={"p-2 rounded-lg bg-white border"}
                  />
                  <div>
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-gray-400">
                      Hora de Envio
                    </span>
                    <span className="text-md font-semibold">
                      {data.proghour}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className=" bg-gray-100 rounded-lg">
                <span className="text-sm font-semibold text-gray-500">
                  Envió Inmediato (al confirmar)
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <_Button
          title="Cancelar"
          style="none"
          onClick={onClose}
          loading={isLoading}
          disabled={isLoading}
        />

        <_Button
          title="Guardar"
          onClick={_onAction}
          style="primary"
          loading={isLoading}
          disabled={isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
};

_ModalConfirmRcs.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onAction: PropTypes.func,
  isLoading: PropTypes.bool,
  data: PropTypes.object,
};

MessagePreview.propTypes = {
  step: PropTypes.number,
  data: PropTypes.object,
};
export default _ModalConfirmRcs;
